import classNames from './TopicAccordion.module.scss';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import SVG from 'react-inlinesvg';
import { useEffect, useRef, useState } from 'react';

import { gameContentService } from '@/school-management/services/gameContent';
import { CanDoAnswer, TopicsActivities } from '@/school-management/types/response/gameContent';
import Rating from '@/components/school-management/StudentDataModal/UnitData/Rating/Rating';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import commonUtils from '@/school-management/utils/common';

import upArrow from '@/assets/svg/up-arrow.svg';
import downArrow from '@/assets/svg/down-arrow.svg';
import performanceTaskIcon from '@/assets/svg/performanceTask.svg';

export interface ActivityProps {
  activityName: string;
  activityType?: string;
  achievedStars: number;
  cumulativeScore: number;
  hasProgress: boolean;
  hasScore?: boolean;
  isSkipped?: boolean;
}

export interface TopicAccordionProps {
  fromToLanguage: string;
  unitId: number;
  topicId: number;
  topicName: string;
  completionPercentage: number;
  completedActivities: number;
  totalActivities: number;
  activitiesProgress: Record<string, any> | null;
  canDoAnswerId: number;
  canDoAnswer: CanDoAnswer | null;
  canDoStatement: string;
  level: number;
}

const TopicAccordion = ({
  fromToLanguage,
  unitId,
  topicId,
  topicName,
  completionPercentage,
  completedActivities,
  totalActivities,
  activitiesProgress,
  canDoAnswer,
  canDoStatement,
  level,
}: TopicAccordionProps) => {
  const { t } = useAdminTranslation();
  const [shouldExpand, setShouldExpand] = useState(false);
  const [activities, setActivities] = useState<ActivityProps[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLoaded = useRef<boolean>(false);
  const isActive = useRef<boolean>(false);

  useEffect(() => {
    isActive.current = true;

    return () => {
      isActive.current = false;
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      if (unitId && topicId && fromToLanguage) {
        setIsLoading(true);

        let activitiesList: TopicsActivities = [];
        try {
          const response =
            (await gameContentService.getTopicActivities(fromToLanguage, level, unitId, topicId))
              ?.data?.data || [];
          activitiesList = response.sort((activity1, activity2) =>
            activity1.order < activity2.order ? -1 : activity1.order < activity2.order ? 1 : 0
          );
        } catch (ex) {
          commonUtils.showToast(t('studentDataModal.unitData.somethingWentWrong'), {
            type: 'error',
          });
        }

        if (!isActive.current) {
          return;
        }

        const activities: ActivityProps[] = activitiesList.map(activity => {
          const activityProgress = activitiesProgress?.[activity.id] || null;

          const activityName: string = activity.title;
          const achievedStars: number = activityProgress?.cumulativeAchievedStars || 0;
          const cumulativeScore: number = activityProgress?.cumulativeScore || 0;
          const hasProgress: boolean = !!activityProgress;
          const hasScore: boolean = activity.hasScore;
          const isSkipped: boolean = activityProgress?.isSkipped;

          return {
            activityName,
            achievedStars,
            cumulativeScore,
            hasProgress,
            hasScore,
            isSkipped,
          };
        });

        setActivities(activities);
        setIsLoading(false);
      }
    };

    if (!isLoaded.current && shouldExpand) {
      isLoaded.current = true;
      init();
    }
  }, [shouldExpand, unitId, fromToLanguage, level, topicId, activitiesProgress, t]);

  const onToggle = () => {
    setShouldExpand(prevState => !prevState);
  };

  const activitiesList = (activities || []).map((activity, index) => {
    const {
      activityName,
      activityType,
      achievedStars,
      cumulativeScore,
      hasProgress,
      hasScore,
      isSkipped,
    } = activity;
    const scoreText = isSkipped
      ? t('studentDataModal.unitData.skipped')
      : hasScore
        ? cumulativeScore < 60
          ? t('studentDataModal.unitData.failed')
          : cumulativeScore
        : t('studentDataModal.unitData.completed');

    return (
      <div key={index} className={classNames.activity}>
        <div className={classNames.content}>
          <span className={classNames.order}>{index + 1}.</span>
          <div className={classNames.partOne}>
            <span className={classNames.name}>
              {activityType === 'Performance Task' && (
                <SVG className={classNames.performanceTask} src={performanceTaskIcon} />
              )}
              {activityName}
            </span>
            <Rating
              className={classNames.rating}
              achievedStars={achievedStars}
              showEmptyStars={false}
            />
          </div>
          <div className={classNames.partTwo}>
            <span className={classNames.type}>{activityType}</span>
            {hasProgress && activityType && <div className={classNames.seperator}></div>}
            {hasProgress && <span className={classNames.score}>{scoreText}</span>}
          </div>
        </div>
        <div className={classNames.sectionSeperator}></div>
      </div>
    );
  });

  const arrowSrc = shouldExpand ? upArrow : downArrow;

  return (
    <div
      className={classes(classNames.topicAccordion, {
        [classNames.open]: shouldExpand,
      })}
    >
      <div className={classNames.top}>
        <div>
          <div className={classNames.nameAndPercentage}>
            <span className={classNames.name}>{topicName + ' '}</span>
            <span className={classNames.percentage}>| {completionPercentage}%</span>
          </div>
          <button
            className={classNames.arrow}
            onClick={onToggle}
            aria-label={t('studentDataModal.unitData.toggle').toString()}
          >
            <SVG src={arrowSrc} />
          </button>
        </div>
        <div className={classNames.totalActivities}>
          <span
            className={classNames.totalActivities}
          >{`${t('studentDataModal.unitData.totalActivities')} ${completedActivities}/${totalActivities} `}</span>
          <div className={classNames.progress}>
            <div className={classNames.bar} style={{ width: `${completionPercentage}%` }}></div>
          </div>
        </div>
      </div>
      <div className={classNames.body}>
        <div className={classNames.activities}>
          {isLoading ? (
            <div className={classNames.loader}>
              <AppLoaderCircle />
            </div>
          ) : (
            <>
              {activitiesList}
              <div className={classNames.canDo}>
                <div className={classNames.partOne}>
                  <span className={classNames.canDoLabel}>
                    {t('studentDataModal.unitData.canDo')}
                  </span>
                  <div className={classNames.seperator}></div>
                  <span className={classNames.canDoStatement}>“{canDoStatement}“</span>
                </div>
                <div className={classNames.partTwo}>
                  {canDoAnswer && (
                    <img
                      className={classNames.canDoAnswerImg}
                      src={canDoAnswer.iconSrc}
                      alt={canDoAnswer.answer}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicAccordion;
